import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  WrenchScrewdriverIcon,
  PlayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PlusIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi, migrationsApi } from '@/lib/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

interface Migration {
  id: string;
  project_id: string;
  name: string;
  version: string;
  description?: string;
  up_sql: string;
  down_sql?: string;
  checksum: string;
  status: 'pending' | 'applied' | 'failed' | 'rolled_back';
  applied_at?: string;
  rolled_back_at?: string;
  execution_time_ms?: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

interface MigrationHistory {
  id: string;
  migration_id: string;
  name: string;
  applied_at: string;
  execution_time_ms: number;
  checksum: string;
}

export default function MigrationsDashboard() {
  const queryClient = useQueryClient();
  const { id: projectId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSQLModal, setShowSQLModal] = useState<Migration | null>(null);
  const [selectedTab, setSelectedTab] = useState<'migrations' | 'history'>('migrations');
  const [newMigration, setNewMigration] = useState({
    name: '',
    sql_up: '',
    sql_down: ''
  });

  // Fetch project details
  const { data: projectResponse } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Fetch migrations for selected project
  const { data: migrationsResponse, isLoading: migrationsLoading } = useQuery(
    ['migrations', projectId],
    () => migrationsApi.getMigrations(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Fetch migration history for selected project
  const { data: historyResponse } = useQuery(
    ['migration-history', projectId],
    () => migrationsApi.getHistory(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Create migration mutation
  const createMigrationMutation = useMutation(
    (migrationData: typeof newMigration) => {
      // Transform form data to match backend API (sql_up -> up_sql, sql_down -> down_sql)
      const apiData = {
        name: migrationData.name,
        up_sql: migrationData.sql_up,
        down_sql: migrationData.sql_down
      };
      return migrationsApi.createMigration(projectId as string, apiData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['migrations', projectId]);
        setShowCreateModal(false);
        setNewMigration({ name: '', sql_up: '', sql_down: '' });
        toast.success('Migration created successfully');
      },
      onError: (_error: any) => {
        toast.error('Failed to create migration');
      },
    }
  );

  // Run migration mutation
  const runMigrationMutation = useMutation(
    (migrationId: string) => 
      migrationsApi.applyMigration(migrationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['migrations', projectId]);
        queryClient.invalidateQueries(['migration-history', projectId]);
        toast.success('Migration applied successfully');
      },
      onError: (_error: any) => {
        toast.error('Failed to apply migration');
      },
    }
  );

  // Rollback migration mutation
  const rollbackMigrationMutation = useMutation(
    (migrationId: string) => 
      migrationsApi.rollbackMigration(migrationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['migrations', projectId]);
        queryClient.invalidateQueries(['migration-history', projectId]);
        toast.success('Migration rolled back successfully');
      },
      onError: (_error: any) => {
        toast.error('Failed to rollback migration');
      },
    }
  );

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const project = projectResponse?.data?.project;
  const migrations: Migration[] = Array.isArray(migrationsResponse?.data) ? migrationsResponse.data : 
                                  Array.isArray(migrationsResponse?.data?.migrations) ? migrationsResponse.data.migrations :
                                  Array.isArray(migrationsResponse?.data?.data) ? migrationsResponse.data.data : [];
  // Transform applied migrations into history format
  const historyMigrations = Array.isArray(historyResponse?.data?.migrations) 
    ? historyResponse.data.migrations 
    : Array.isArray(historyResponse?.data?.data?.migrations)
    ? historyResponse.data.data.migrations
    : [];
  
  const history: MigrationHistory[] = historyMigrations
    .filter((m: Migration) => m.status === 'applied' && m.applied_at)
    .map((m: Migration) => ({
      id: m.id,
      migration_id: m.id,
      name: m.name,
      applied_at: m.applied_at!,
      execution_time_ms: m.execution_time_ms || 0,
      checksum: m.checksum
    }));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'applied': return CheckCircleIcon;
      case 'failed': return ExclamationTriangleIcon;
      case 'pending': return ClockIcon;
      default: return ClockIcon;
    }
  };

  const tabs = [
    { id: 'migrations', name: 'Migrations', icon: WrenchScrewdriverIcon },
    { id: 'history', name: 'History', icon: ClockIcon },
  ];

  return (
      <div className="px-4 sm:px-0">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">
              Database Migrations
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage database schema changes and track migration history for {project?.name || 'this project'}.
            </p>
          </div>
          {projectId && (
            <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Migration
              </button>
            </div>
          )}
        </div>

        {project && (
          <>
            {/* Tab Navigation */}
            <div className="mt-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id as any)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                      selectedTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="h-4 w-4 mr-2" />
                    {tab.name} ({tab.id === 'migrations' ? (migrations?.length || 0) : (history?.length || 0)})
                  </button>
                ))}
              </nav>
            </div>

            {/* Migrations Tab */}
            {selectedTab === 'migrations' && (
              <div className="mt-6">
                {migrationsLoading ? (
                  <div className="animate-pulse space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-20 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                ) : (migrations?.length || 0) === 0 ? (
                  <div className="text-center py-12 card">
                    <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No migrations found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Get started by creating your first database migration.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {migrations.map((migration) => {
                      const StatusIcon = getStatusIcon(migration.status);
                      return (
                        <div key={migration.id} className="card">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <StatusIcon className="h-5 w-5 text-gray-400 mr-3" />
                                <div>
                                  <h3 className="text-sm font-medium text-gray-900">
                                    {migration.name}
                                  </h3>
                                  <div className="flex items-center mt-1 space-x-4">
                                    <span className="text-xs text-gray-500">
                                      Version: {migration.version}
                                    </span>
                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(migration.status)}`}>
                                      {migration.status.toUpperCase()}
                                    </span>
                                    {migration.applied_at && (
                                      <span className="text-xs text-gray-500">
                                        Applied {formatDistanceToNow(new Date(migration.applied_at), { addSuffix: true })}
                                      </span>
                                    )}
                                  </div>
                                  {migration.error_message && (
                                    <p className="mt-1 text-xs text-red-600">
                                      {migration.error_message}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setShowSQLModal(migration)}
                                className="btn-secondary"
                              >
                                <EyeIcon className="h-4 w-4 mr-2" />
                                View SQL
                              </button>
                              
                              {migration.status === 'pending' && (
                                <button
                                  onClick={() => runMigrationMutation.mutate(migration.id)}
                                  disabled={runMigrationMutation.isLoading}
                                  className="btn-primary disabled:opacity-50"
                                >
                                  <PlayIcon className="h-4 w-4 mr-2" />
                                  Apply
                                </button>
                              )}
                              
                              {migration.status === 'applied' && migration.down_sql && (
                                <button
                                  onClick={() => rollbackMigrationMutation.mutate(migration.id)}
                                  disabled={rollbackMigrationMutation.isLoading}
                                  className="btn-secondary disabled:opacity-50"
                                >
                                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                                  Rollback
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* History Tab */}
            {selectedTab === 'history' && (
              <div className="mt-6 card">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Migration History</h2>
                
                {(history?.length || 0) === 0 ? (
                  <div className="text-center py-8">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No history available</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Migration history will appear here after applying migrations.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Migration
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Applied At
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Execution Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Checksum
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {history.map((entry) => (
                          <tr key={entry.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {entry.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDistanceToNow(new Date(entry.applied_at), { addSuffix: true })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {entry.execution_time_ms}ms
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                              {entry.checksum.substring(0, 8)}...
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Create Migration Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Migration</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Migration Name</label>
                    <input
                      type="text"
                      value={newMigration.name}
                      onChange={(e) => setNewMigration({ ...newMigration, name: e.target.value })}
                      className="mt-1 form-input w-full"
                      placeholder="e.g., add_user_profiles_table"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Up SQL (Applied)</label>
                    <textarea
                      value={newMigration.sql_up}
                      onChange={(e) => setNewMigration({ ...newMigration, sql_up: e.target.value })}
                      className="mt-1 form-textarea w-full font-mono text-sm"
                      rows={8}
                      placeholder="CREATE TABLE user_profiles (..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Down SQL (Rollback)</label>
                    <textarea
                      value={newMigration.sql_down}
                      onChange={(e) => setNewMigration({ ...newMigration, sql_down: e.target.value })}
                      className="mt-1 form-textarea w-full font-mono text-sm"
                      rows={4}
                      placeholder="DROP TABLE user_profiles;"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => createMigrationMutation.mutate(newMigration)}
                    disabled={createMigrationMutation.isLoading || !newMigration.name || !newMigration.sql_up}
                    className="btn-primary disabled:opacity-50"
                  >
                    {createMigrationMutation.isLoading ? 'Creating...' : 'Create Migration'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SQL Modal */}
        {showSQLModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {showSQLModal.name} - SQL
                  </h3>
                  <button
                    onClick={() => setShowSQLModal(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="sr-only">Close</span>
                    ✕
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Up SQL (Apply)</h4>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-auto">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                        {showSQLModal.up_sql}
                      </pre>
                    </div>
                  </div>

                  {showSQLModal.down_sql && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Down SQL (Rollback)</h4>
                      <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-auto">
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                          {showSQLModal.down_sql}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => setShowSQLModal(null)}
                    className="btn-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}